import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

export interface PayOutModalContextState {
  isVisible: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const defaultState: PayOutModalContextState = {
  isVisible: false,
  open: () => undefined,
  close: () => undefined,
  toggle: () => undefined,
};

export const PayOutModalContext = React.createContext(defaultState);

export const usePayOutModal = () => useContext(PayOutModalContext);

export const PayOutModalContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [isVisible, setIsVisible] = useState(defaultState.isVisible);
  const location = useLocation();

  const open = useCallback(() => setIsVisible(true), []);
  const close = useCallback(() => setIsVisible(false), []);
  const toggle = useCallback(() => setIsVisible((prev) => !prev), []);

  useEffect(() => {
    close();
  }, [location.pathname]);

  const state = useMemo(
    () => ({
      isVisible,
      open,
      close,
      toggle,
    }),
    [isVisible]
  );

  return (
    <PayOutModalContext.Provider value={state}>
      {children}
    </PayOutModalContext.Provider>
  );
};

export const withPayOutModalContext = <P extends {}>(
  Component: React.ComponentType<P>
) => {
  return (props: P) => {
    return (
      <PayOutModalContextProvider>
        <Component {...props} />
      </PayOutModalContextProvider>
    );
  };
};
